'use client';

import { useQuery } from '@tanstack/react-query';
import { gatedAction } from '@/lib/api/auth-gate';
import { accountKeys } from '@/lib/query-keys';
import { getActivationSummaryAction } from './actions';
import type { ActivationSummaryResponseDto } from './types';

/**
 * Hook reading the resolved activation-page summary from
 * GET /account/billing/activation-summary (LF-4086).
 *
 * The BE resolves the firm's plan/price (override-aware), per-matter fee, gift
 * block and projected dates, so the trial-gate page renders without re-deriving
 * the tier or computing GST. Fetched only when the gate actually applies — pass
 * `enabled: false` to skip the request for firms that won't see the gate.
 */
export function useActivationSummary(options?: { enabled?: boolean }) {
  return useQuery<ActivationSummaryResponseDto>({
    queryKey: accountKeys.billingActivationSummary,
    queryFn: () => gatedAction(() => getActivationSummaryAction()),
    enabled: options?.enabled ?? true,
    // Always refetch on mount — ops can change the firm's pricing (Enterprise
    // subscription / per-matter fee / complimentary-gift matters) right before the
    // firm activates, so the gate must show the latest, never a cached value
    // (LF-4531). The endpoint is served `Cache-Control: no-store` to match.
    staleTime: 0,
    refetchOnMount: 'always'
  });
}
